/**
 * Ripple timing, easing and blot bookkeeping.
 *
 * Adapted from react-ink 6.5.4 (https://github.com/vigetlabs/react-ink),
 * MIT License, Copyright (c) 2015 Viget Labs.
 */

export interface Blot {
  /** Animation duration in ms. */
  duration: number;
  /** Timestamp (ms) the press started. */
  mouseDown: number;
  /** Timestamp (ms) the press was released, or 0 while still held. */
  mouseUp: number;
  /** Full radius the blot grows to, in CSS px. */
  radius: number;
  /** Press origin, relative to the canvas, in CSS px. */
  x: number;
  y: number;
}

const SQRT_2 = Math.sqrt(2);

/**
 * @param t current time of the tween
 * @param b beginning value
 * @param c change between the beginning and destination value
 * @param d total time of the tween
 */
function easeOutQuint(t: number, b: number, c: number, d: number) {
  const p = t / d - 1;
  return c * (p * p * p * p * p + 1) + b;
}

function getPress(blot: Blot) {
  return Math.min(blot.duration, Date.now() - blot.mouseDown);
}

function getRelease(blot: Blot) {
  return blot.mouseUp > 0 ? Date.now() - blot.mouseUp : 0;
}

function getRadius(blot: Blot) {
  const { duration, radius } = blot;
  const down = easeOutQuint(getPress(blot), 0, radius, duration) * 0.85;
  const up = easeOutQuint(getRelease(blot), 0, radius, duration) * 0.15;
  const undulation = radius * 0.02 * Math.cos(Date.now() / duration);
  return Math.max(0, down + up + undulation);
}

export function getMaxRadius(height: number, width: number, radius?: number) {
  return radius || Math.max(height, width) * 0.6;
}

export function getBlotOpacity(blot: Blot, opacity: number) {
  return easeOutQuint(getRelease(blot), opacity, -opacity, blot.duration);
}

function getBlotOuterOpacity(blot: Blot, opacity: number) {
  return Math.min(getBlotOpacity(blot, opacity), easeOutQuint(getPress(blot), 0, 0.3, blot.duration * 3));
}

export function getBlotShiftX(blot: Blot, size: number, width: number) {
  return Math.min(1, ((getRadius(blot) / size) * 2) / SQRT_2) * (width / 2 - blot.x);
}

export function getBlotShiftY(blot: Blot, size: number, height: number) {
  return Math.min(1, ((getRadius(blot) / size) * 2) / SQRT_2) * (height / 2 - blot.y);
}

export function getBlotScale(blot: Blot) {
  return getRadius(blot) / blot.radius;
}

export interface RippleStore {
  blots(): readonly Blot[];
  add(blot: Blot): void;
  /** Releases the most recent held blot. */
  release(time: number): void;
  stop(): void;
  getTotalOpacity(opacity: number): number;
}

/**
 * Tracks live blots and runs a requestAnimationFrame loop while any exist, calling
 * `onFrame` each tick (and once more after the last blot expires, to clear).
 */
export function createRippleStore(onFrame: () => void): RippleStore {
  let data: Blot[] = [];
  let playing = false;
  let frame = 0;

  const isLive = ({ mouseUp, duration }: Blot) => !mouseUp || Date.now() - mouseUp < duration;

  const update = () => {
    data = data.filter(isLive);
    if (data.length) {
      frame = requestAnimationFrame(update);
    } else {
      stop();
    }
    onFrame();
  };

  const play = () => {
    if (!playing) {
      playing = true;
      update();
    }
  };

  const stop = () => {
    playing = false;
    cancelAnimationFrame(frame);
  };

  return {
    blots: () => data,
    add(blot) {
      data.push(blot);
      play();
    },
    release(time) {
      for (let i = data.length - 1; i >= 0; i--) {
        if (!data[i].mouseUp) {
          data[i].mouseUp = time;
          return;
        }
      }
    },
    stop,
    getTotalOpacity(opacity) {
      return data.reduce((total, blot) => total + getBlotOuterOpacity(blot, opacity), 0);
    },
  };
}
